import { gold } from '@ant-design/colors';
import { EditOutlined, WarningTwoTone } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { Alert, App, Modal, Space } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { useEffect } from 'react';
import { LoadingIndicator } from '../../../../components/LoadingIndicator.tsx';
import type { CatalogParameterUpdateInput } from '../../../../graphql/generated/graphql.ts';
import { CatalogParameterForm } from './CatalogParameterForm';
import { MULTI_CATALOG_PARAMETERS_QUERY, UPDATE_CATALOG_PARAMETERS_MUTATION } from './UpdateCatalogParametersModal.gql';

export const UpdateCatalogParametersModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
  labId: string;
  cpIds: string[];
  visible: boolean;
}> = ({ onClose, onSuccess, labId, cpIds, visible }) => {
  const [updateCatalogParametersMutation] = useMutation(UPDATE_CATALOG_PARAMETERS_MUTATION);
  const [form] = useForm<CatalogParameterUpdateInput>();
  const { message } = App.useApp();

  const update = async (values: CatalogParameterUpdateInput) => {
    try {
      await updateCatalogParametersMutation({
        variables: {
          ids: cpIds,
          input: {
            ...values,
            insuranceDefinitions: form.getFieldValue('insuranceDefinitions'),
          },
        },
      });

      message.success('Katalog-Parameter wurden aktualisiert');
      onSuccess();
      onClose();
    } catch (_e) {
      message.error('Katalog-Parameter konnten nicht aktualisiert werden');
    }
  };

  const { data, loading } = useQuery(MULTI_CATALOG_PARAMETERS_QUERY, {
    variables: {
      ids: cpIds,
    },
    skip: !cpIds.length,
  });

  useEffect(() => {
    let catalogParameter = null;
    if (data?.individualCatalogParameters?.length === 1) {
      catalogParameter = data.individualCatalogParameters[0];
    }

    form.setFieldsValue({
      insuranceDefinitions:
        catalogParameter?.insuranceDefinitions.map(it => ({
          insuranceId: it.insurance.id,
          diagnoseIds: it.diagnoses.map(d => d.id),
          lg: it.lg,
          periodicGroupId: it.periodicGroup?.id,
          deductible: it.deductible,
        })) ?? [],
      specialRateIds: catalogParameter?.specialRates.map(sr => sr.id) ?? [],
      doctorBilling: catalogParameter?.doctorBilling ?? false,
      patientBilling: catalogParameter?.patientBilling ?? false,
      usePatientBilling: catalogParameter?.usePatientBilling ?? false,
      useDoctorBilling: catalogParameter?.useDoctorBilling ?? false,
      useInsuranceDefinitions: catalogParameter?.useInsuranceDefinitions ?? false,
      useSpecialRates: catalogParameter?.useSpecialRates ?? false,
      useSpecimens: catalogParameter?.useSpecimens ?? false,
      specimenIds: catalogParameter?.specimens.map(sr => sr.id) ?? [],
      lgDoctorBilling: catalogParameter?.lgDoctorBilling ?? false,
      useLgDoctorBilling: catalogParameter?.useLgDoctorBilling ?? false,
      useLgInsuranceDefinitions: catalogParameter?.useLgInsuranceDefinitions ?? false,
      useForms: catalogParameter?.useForms ?? false,
      formIds: catalogParameter?.forms.map(it => it.id) ?? [],
      usePricePatient: catalogParameter?.usePricePatient ?? false,
      usePriceDoctor: catalogParameter?.usePriceDoctor ?? false,
      usePriceLgDoctor: catalogParameter?.usePriceLgDoctor ?? false,
      usePriceDeductible: catalogParameter?.usePriceDeductible ?? false,
      pricePatient: catalogParameter?.pricePatient ?? null,
      priceDoctor: catalogParameter?.priceDoctor ?? null,
      priceLgDoctor: catalogParameter?.priceLgDoctor ?? null,
      priceDeductible: catalogParameter?.priceDeductible ?? null,
    });
  }, [form, data]);

  return (
    <Modal
      width={1024}
      title={
        data?.individualCatalogParameters?.length === 1 ? (
          <>
            Katalog-Parameter »<span className={monoFont}>{data.individualCatalogParameters[0].parameter.shortName}</span>« bearbeiten
          </>
        ) : loading ? (
          'Bitte warten...'
        ) : (
          <Space
            orientation="vertical"
            className={css`
              width: 100%;
            `}
          >
            Batch-Bearbeitung mehrerer Katalog-Parameter
            <Alert
              title="Achtung! Alle Werte werden überschrieben. Es müssen alle Werte gesetzt werden."
              type="warning"
              showIcon
              icon={<WarningTwoTone twoToneColor={gold[5]} />}
            />
          </Space>
        )
      }
      okText={data?.individualCatalogParameters?.length === 1 ? 'Aktualisieren' : 'Alle aktualisieren'}
      okButtonProps={{ disabled: loading, loading: loading, icon: <EditOutlined /> }}
      open={visible}
      onCancel={onClose}
      onOk={form.submit}
      destroyOnHidden
    >
      {loading ? <LoadingIndicator height="200px" /> : <CatalogParameterForm form={form} onFinish={update} labId={labId} />}
    </Modal>
  );
};
